import Select from "react-select"
import moment from "moment-timezone"
import { SelectOption } from "../../types/SelectOption"
import React, { useEffect, useMemo, useState } from "react"
import lodash from "lodash"
import Utils from "../../Utils"
import BackOfficeAPIClient from "../../clients/BackofficeAPIClient"
import { Site } from "../../types/Site"

export default function List() {
    const backofficeApiClient = new BackOfficeAPIClient()

    const [ globalTimezone, setGlobalTimezone ] = useState<string>()
    const [ timezoneByDealerships, setTimezoneByDealerships ] = useState<Record<number, string>>({})
    const [ sites, setSites ] = useState<Site[]>([])
    const [ isSaving, setIsSaving ] = useState(false)

    const fetchSitesEtSocietes = async () => {
        try {
            const json = await backofficeApiClient.get<Site[]>('/sites')

            let fetchedSites = lodash.sortBy(json, (site) => {
                return site.Plaque_Libelle
            })

            if (Utils.isAccesClient()) {
                fetchedSites = lodash.filter(fetchedSites, (d) => {
                    return !d.EstFictif
                })
            }

            setSites(fetchedSites)
        } catch (e) {
            console.error(e)
        }
    }

    const fetchTimezoneConfigurations = async () => {
        try {
            const configurations = await backofficeApiClient.get<{siteId: number, timezone: string}[]>('/fuseauHoraire')
            const timezonesBySite: Record<number, string> = {}

            configurations.forEach((config: {siteId: number, timezone: string}) => {
                if (config.siteId === null) {
                    setGlobalTimezone(config.timezone)
                } else {
                    timezonesBySite[config.siteId] = config.timezone
                }
            })

            setTimezoneByDealerships(timezonesBySite)
        } catch (e) {
            console.error('Erreur lors du chargement des configurations de timezone:', e)
        }
    }

    const handleSaveAll = async () => {
        setIsSaving(true)
        try {
            const configurationsToSave = []

            if (globalTimezone) {
                configurationsToSave.push({
                    siteId: null,
                    timezone: globalTimezone,
                })
            }

            Object.entries(timezoneByDealerships).forEach(([siteId, timezone]) => {
                configurationsToSave.push({
                    siteId: parseInt(siteId),
                    timezone,
                })
            })

            await backofficeApiClient.post('/fuseauHoraire', { configurations: configurationsToSave })
        } catch (e) {
            console.error('Erreur lors de la sauvegarde des configurations:', e)
            alert('Erreur lors de la sauvegarde')
        } finally {
            setIsSaving(false)
        }
    }

    const handleSiteTimezoneChange = (siteId: number, timezone: string) => {
        setTimezoneByDealerships(prev => ({
            ...prev,
            [siteId]: timezone,
        }))
    }

    const timezones = useMemo(() => {
        return moment.tz.names().map((tz) => {
            return { value: tz as string, label: tz } as SelectOption
        }) ?? []
    }, [])

    useEffect(() => {
        fetchSitesEtSocietes()
        fetchTimezoneConfigurations()
    }, [])

    return (
        <div className="tw-flex tw-flex-col tw-p-4 tw-gap-5">
            <div className="tw-flex tw-flex-col tw-gap-3">
                <div className="tw-flex tw-flex-row tw-justify-between">
                    <h2 className="tw-font-bold tw-text-xxl">Configuration globale</h2>
                    <button
                        type="button"
                        onClick={handleSaveAll}
                        className="btn btn-primary"
                        disabled={isSaving || (!globalTimezone && Object.keys(timezoneByDealerships).length === 0)}
                    >
                        {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
                <div className="tw-flex tw-flex-row tw-gap-4 tw-items-center tw-pl-5">
                    <span className="tw-w-32">Fuseau Horaire :</span>
                    <Select
                        isMulti={false}
                        style={{ minWidth: '15em' }}
                        value={timezones
                            .find((opt) => opt.value === globalTimezone)
                        }
                        onChange={(option: SelectOption) => {
                            if (option?.value) {
                                setGlobalTimezone(option.value as string)
                            }
                        }}
                        options={timezones}
                    />
                </div>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-3">
                <h2 className="tw-font-bold tw-text-xxl">Configuration par site</h2>
                <table className="table table-striped table-hover table-sm">
                    <thead>
                    <tr>
                        <th>Société</th>
                        <th>Site</th>
                        <th>Fuseau Horaire</th>
                    </tr>
                    </thead>
                    <tbody>
                    {sites.map((site) => {
                        return (
                            <tr key={site.IDSite}>
                                <td>{site.Plaque_Libelle}</td>
                                <td>{site.Descr}</td>
                                <td>
                                    <Select
                                        isMulti={false}
                                        value={timezones.find((opt) => opt.value === timezoneByDealerships[site.IDSite])}
                                        onChange={(option: SelectOption | null) => {
                                            if (option?.value) {
                                                handleSiteTimezoneChange(site.IDSite, option.value as string)
                                            } else {
                                                setTimezoneByDealerships(prev => {
                                                    const { [site.IDSite]: _, ...rest } = prev
                                                    return rest
                                                })
                                            }
                                        }}
                                        options={timezones}
                                        placeholder="Sélectionner un fuseau horaire"
                                        isClearable
                                    />
                                </td>
                            </tr>
                        )
                    })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
